/**
 * Whether the workshop detail page is read-only for this viewer.
 *
 * Two reasons it can be, and they are different questions:
 *
 *  - TEACHER MODE is read-only by definition. The teacher tree shows a
 *    teacher their own assignments; editing the record is not theirs.
 *  - REGION BREADTH. A Regional Director holds `read:all_regions`, so they can
 *    open a workshop in a region they are not assigned to — and
 *    `breadth_write:assigned` means the server will refuse the save. The
 *    regional dashboards shell used to carry that answer in its envelope; with
 *    the shell retired the page derives it the same way the create drawer's
 *    region picker does, from the grants and the workshop's own region.
 *
 * Deriving it beats showing an Edit button that 403s: the affordance is the
 * promise, and this is the same rule the server enforces rather than a second
 * one that could disagree.
 */
import { useViewerRegions } from '../../../../hooks/use-viewer-regions';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';

import { useWorkshopViewMode } from './workshopDetailContext';

export function useWorkshopReadOnly(venueRegionId: string | null | undefined): boolean {
  const { hasPermission } = usePermissions();
  const { scopeRegionIds } = useViewerRegions();
  const mode = useWorkshopViewMode();

  if (mode === 'teacher') return true;

  // Positively established, never inferred from an absent permission:
  // `hasPermission` answers false while the token is still loading, so testing
  // for "not nation-wide" would briefly lock every viewer out of their own
  // region. Requiring the assigned-breadth grant explicitly means the ambiguous
  // states fall through to writable, which is safe because the server refuses
  // the write regardless.
  const nationWide = hasPermission(Permission.BREADTH_WRITE_ALL);
  const confinedToOwn = !nationWide && hasPermission(Permission.BREADTH_WRITE_ASSIGNED);
  if (!confinedToOwn) return false;

  // A workshop whose venue carries no region cannot be matched against the
  // viewer's set, so it stays writable and the server decides.
  return venueRegionId ? !scopeRegionIds.includes(venueRegionId) : false;
}
